import { useMemo } from 'react';
import { themes, type AccentTokens } from '@circles/tokens';
import { deriveAccent } from '@circles/tokens/accent';
import circlesLogo from '../../../brand/circles-logo.svg';
import nordwindLogo from '../../../brand/samples/nordwind-logo.svg';
import nordwindHeader from '../../../brand/samples/nordwind-header.svg';
import praxisLogo from '../../../brand/samples/praxis-logo.svg';
import praxisHeader from '../../../brand/samples/praxis-header.svg';
import { useStore, type Mode } from './store';

const assets: Record<string, string> = {
  'circles-logo.svg': circlesLogo,
  'nordwind-logo.svg': nordwindLogo,
  'nordwind-header.svg': nordwindHeader,
  'praxis-logo.svg': praxisLogo,
  'praxis-header.svg': praxisHeader,
};

export interface ActiveTheme {
  id: string;
  /** Organisation name, or null in the neutral Circles design. */
  orgName: string | null;
  logo: string | null;
  monogram?: string;
  feedHeader: string | null;
  accent: AccentTokens;
  mode: Mode;
}

export const circlesLogoUrl = circlesLogo;

export function resolveTheme(
  orgId: string | null,
  customAccent: string | null,
  mode: Mode,
): ActiveTheme {
  if (!orgId) {
    return {
      id: 'circles',
      orgName: null,
      logo: circlesLogo,
      feedHeader: null,
      accent: themes.circles.accent[mode],
      mode,
    };
  }
  if (orgId === 'custom' && customAccent) {
    return {
      id: 'custom',
      orgName: 'Your organisation',
      logo: null,
      monogram: 'Y',
      feedHeader: null,
      accent: deriveAccent(customAccent)[mode],
      mode,
    };
  }
  const t = themes[orgId as keyof typeof themes] ?? themes.circles;
  return {
    id: t.id,
    orgName: t.id === 'circles' ? null : t.name,
    logo: assets[t.logo] ?? null,
    feedHeader: t.feedHeader ? (assets[t.feedHeader] ?? null) : null,
    accent: t.accent[mode],
    mode,
  };
}

export function useActiveTheme(): ActiveTheme {
  const orgId = useStore((s) => s.orgId);
  const custom = useStore((s) => s.customAccent);
  const mode = useStore((s) => s.mode);
  return useMemo(() => resolveTheme(orgId, custom, mode), [orgId, custom, mode]);
}

/** CSS custom properties for an accent, applied to the device screen. */
export function accentStyle(accent: AccentTokens): Record<string, string> {
  const kebab = (k: string) => k.replace(/([A-Z])/g, '-$1').toLowerCase();
  return Object.fromEntries(
    Object.entries(accent).map(([k, v]) => [`--color-accent-${kebab(k)}`, v]),
  );
}
